import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import PermissionChangeDialog from '../components/PermissionChangeDialog';
import { ROLES } from '../services/roles';
import {
  getCurrentUser,
  refreshCurrentUser,
  isLocalAuthentication,
  logout as cognitoLogout,
} from '../services/auth';
import { getMe } from '../services/api';

const AuthContext = createContext(null);
const PERMISSION_SNAPSHOT_KEY = 'chatty_permission_snapshot';
const AUTH_RESTORE_RETRY_MS = 5_000;
const AUTH_RESTORE_MAX_RETRY_MS = 60_000;
const PERMISSION_POLL_MS = 10_000;

function getAuthRetryDelay(retryCount) {
  const delay = Math.min(
    AUTH_RESTORE_RETRY_MS * 2 ** Math.min(retryCount, 10),
    AUTH_RESTORE_MAX_RETRY_MS
  );
  return Math.min(
    Math.round(delay * (0.8 + Math.random() * 0.4)),
    AUTH_RESTORE_MAX_RETRY_MS
  );
}

function readPermissionSnapshot() {
  try {
    const snapshot = JSON.parse(sessionStorage.getItem(PERMISSION_SNAPSHOT_KEY));
    return snapshot?.username && ROLES.includes(snapshot.role) ? snapshot : null;
  } catch {
    return null;
  }
}

function savePermissionSnapshot(snapshot) {
  try {
    if (snapshot) {
      sessionStorage.setItem(PERMISSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
    } else {
      sessionStorage.removeItem(PERMISSION_SNAPSHOT_KEY);
    }
  } catch {
    // Permission updates must still work when browser storage is unavailable.
  }
}

function isTerminalAuthenticationError(error) {
  const code = error?.code || error?.name;
  return ['NotAuthorizedException', 'UserNotFoundException'].includes(code);
}

function isRetryableAuthenticationError(error) {
  const code = error?.code || error?.name;
  const isFetchFailure = error instanceof TypeError && [
    'Failed to fetch',
    'NetworkError when attempting to fetch resource.',
    'Load failed',
  ].includes(error.message);

  return [
    'NetworkError',
    'NetworkingError',
    'TimeoutError',
    'TooManyRequestsException',
    'ThrottlingException',
    'LimitExceededException',
  ].includes(code) || isFetchFailure || error?.retryable === true ||
    error?.$retryable === true ||
    (error?.$retryable != null && typeof error.$retryable === 'object');
}

/**
 * Return the user with its live role, dashboard tabs and manageable roles
 * from GET /api/v1/me. If /me fails, keep the user signed in but grant no
 * dashboard access; the backend gates every admin call anyway.
 */
function applyMePermissions(user, me) {
  if (!me) return { tabs: [], manageableRoles: [], ...user };
  return { ...user, role: me.role, tabs: me.tabs, manageableRoles: me.manageable_roles };
}

function handlePermissionsError(user, error) {
  console.error('Failed to load permissions', error);
  return { ...user, tabs: [], manageableRoles: [] };
}

function resolvePermissions(user) {
  try {
    const meOrPromise = getMe?.();
    if (!meOrPromise || typeof meOrPromise.then !== 'function') {
      return applyMePermissions(user, meOrPromise);
    }
    return meOrPromise.then(
      (me) => applyMePermissions(user, me),
      (error) => handlePermissionsError(user, error)
    );
  } catch (error) {
    return handlePermissionsError(user, error);
  }
}

export async function withPermissions(user) {
  return resolvePermissions(user);
}

function RouteChangeListener({ onRouteChange }) {
  const location = useLocation();
  useEffect(() => {
    onRouteChange();
  }, [location.key, onRouteChange]);
  return null;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [authRestoreError, setAuthRestoreError] = useState(null);
  const [authRetryKey, setAuthRetryKey] = useState(0);
  const authVersionRef = useRef(0);
  const snapshotRef = useRef(null);
  const [permissionChange, setPermissionChange] = useState(null);
  const checkPermissionsRef = useRef(null);
  const inRouter = useInRouterContext();
  const handleRouteChange = useCallback(() => {
    checkPermissionsRef.current?.();
  }, []);

  const applyCurrentUser = useCallback((currentUser) => {
    const previous = snapshotRef.current || readPermissionSnapshot();
    const sameUser = previous?.username === currentUser.username;
    let notification = sameUser ? previous.notification || null : null;

    if (sameUser && previous.role !== currentUser.role) {
      notification = {
        previousRole: notification?.previousRole || previous.role,
        currentRole: currentUser.role,
      };
    }

    const snapshot = {
      username: currentUser.username,
      role: currentUser.role,
      notification,
    };
    snapshotRef.current = snapshot;
    savePermissionSnapshot(snapshot);
    setPermissionChange(notification);
    setUser(currentUser);
  }, []);

  const acknowledgePermissionChange = useCallback(() => {
    if (snapshotRef.current) {
      const snapshot = { ...snapshotRef.current, notification: null };
      snapshotRef.current = snapshot;
      savePermissionSnapshot(snapshot);
    }
    setPermissionChange(null);
  }, []);

  const retryAuthRestore = useCallback(() => {
    setAuthRetryKey(key => key + 1);
  }, []);

  const logout = useCallback(() => {
    authVersionRef.current += 1;
    snapshotRef.current = null;
    savePermissionSnapshot(null);
    setPermissionChange(null);
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (
        key === 'chat_session_id' ||
        key?.startsWith('chat_messages_') ||
        key?.startsWith('chat_rooms_') ||
        key?.startsWith('chat_current_')
      ) {
        sessionStorage.removeItem(key);
      }
    }

    cognitoLogout();
    setUser(null);
    setAuthRestoreError(null);
    setIsAuthChecked(true);
  }, []);

  // リロード時に最新権限を取得。
  useEffect(() => {
    let cancelled = false;
    let retryTimeout;
    let retryCount = 0;
    const authVersion = authVersionRef.current;

    const canApplyResult = () =>
      !cancelled &&
      authVersion === authVersionRef.current;

    const restoreCurrentUser = async () => {
      if (!canApplyResult()) return;

      try {
        const loadSessionUser = refreshCurrentUser || getCurrentUser;
        const currentUser = await loadSessionUser();
        if (!canApplyResult()) return;
        setAuthRestoreError(null);

        if (currentUser?.passwordChangeRequired) {
          logout();
        } else if (currentUser) {
          const resolved = resolvePermissions(currentUser);
          const loadedUser = typeof resolved?.then === 'function' ? await resolved : resolved;
          if (!canApplyResult()) return;
          applyCurrentUser(loadedUser);
        } else {
          logout();
        }
        setIsAuthChecked(true);
      } catch (error) {
        if (!canApplyResult()) return;

        console.error('Failed to refresh current user', error);
        if (isTerminalAuthenticationError(error)) {
          // 認証が無効な場合はログアウト
          logout();
        } else if (isRetryableAuthenticationError(error)) {
          // 一時的なエラーは認証情報を保持して再試行
          const delay = getAuthRetryDelay(retryCount);
          retryCount += 1;

          retryTimeout = setTimeout(
            restoreCurrentUser, delay
          );
        } else {
          // Keep credentials and let the user retry unexpected failures.
          setAuthRestoreError(error || new Error('Authentication restoration failed'));
        }
      }
    };

    setIsAuthChecked(false);
    setAuthRestoreError(null);
    restoreCurrentUser();

    return () => {
      cancelled = true;
      clearTimeout(retryTimeout);
    };
  }, [applyCurrentUser, authRetryKey, logout]);

  const login = (role, username) => {
    authVersionRef.current += 1;
    const authVersion = authVersionRef.current;
    const snapshot = { username, role, notification: null };
    snapshotRef.current = snapshot;
    savePermissionSnapshot(snapshot);
    setPermissionChange(null);
    // Sign in at once; dashboard access follows when /me answers.
    setUser({ role, username, tabs: [], manageableRoles: [] });
    setAuthRestoreError(null);
    setIsAuthChecked(true);
    withPermissions({ role, username }).then((loaded) => {
      if (authVersion !== authVersionRef.current) return;
      const loadedSnapshot = { username: loaded.username, role: loaded.role, notification: null };
      snapshotRef.current = loadedSnapshot;
      savePermissionSnapshot(loadedSnapshot);
      setUser(current => (current?.username === username ? loaded : current));
    });
  };

  const isLoggedIn = Boolean(user);
  const username = user?.username;

  useEffect(() => {
    if (!isLoggedIn || isLocalAuthentication()) return;

    let cancelled = false;
    let inFlight = false;
    const authVersion = authVersionRef.current;

    const refreshPermissions = async () => {
      if (cancelled || inFlight || authVersion !== authVersionRef.current) return;
      inFlight = true;

      try {
        const loadSessionUser = refreshCurrentUser || getCurrentUser;
        const currentUser = await loadSessionUser();
        if (
          cancelled ||
          authVersion !== authVersionRef.current
        ) {
          return;
        }
        if (!currentUser || currentUser.passwordChangeRequired) {
          logout();
          return;
        }
        const resolved = resolvePermissions(currentUser);
        const loadedUser = typeof resolved?.then === 'function' ? await resolved : resolved;
        if (
          cancelled ||
          authVersion !== authVersionRef.current
        ) {
          return;
        }
        applyCurrentUser(loadedUser);
      } catch (error) {
        if (
          !cancelled &&
          authVersion === authVersionRef.current
        ) {
          console.error('Failed to refresh user permissions:', error);

          if (isTerminalAuthenticationError(error)) {
            logout();
          }
        }
      } finally {
        inFlight = false;
      }
    };

    const checkOnActivity = refreshPermissions;
    const checkWhenVisible = () => {
      if (document.visibilityState === 'visible') checkOnActivity();
    };

    const interval = setInterval(checkWhenVisible, PERMISSION_POLL_MS);
    checkPermissionsRef.current = checkOnActivity;
    document.addEventListener('pointerdown', checkOnActivity, true);
    document.addEventListener('pointermove', checkOnActivity, true);
    document.addEventListener('mousemove', checkOnActivity, true);
    document.addEventListener('click', checkOnActivity, true);
    document.addEventListener('keydown', checkOnActivity, true);
    ['wheel', 'touchmove', 'scroll'].forEach((type) =>
      document.addEventListener(type, checkOnActivity, { capture: true, passive: true })
    );
    document.addEventListener('visibilitychange', checkWhenVisible);
    window.addEventListener('focus', checkOnActivity);

    return () => {
      cancelled = true;
      clearInterval(interval);
      checkPermissionsRef.current = null;
      document.removeEventListener('pointerdown', checkOnActivity, true);
      document.removeEventListener('pointermove', checkOnActivity, true);
      document.removeEventListener('mousemove', checkOnActivity, true);
      document.removeEventListener('click', checkOnActivity, true);
      document.removeEventListener('keydown', checkOnActivity, true);
      ['wheel', 'touchmove', 'scroll'].forEach((type) =>
        document.removeEventListener(type, checkOnActivity, true)
      );
      document.removeEventListener('visibilitychange', checkWhenVisible);
      window.removeEventListener('focus', checkOnActivity);
    };
  }, [isLoggedIn, username, logout, applyCurrentUser]);

  return (
    <AuthContext.Provider value={{ user, isAuthChecked, authRestoreError, retryAuthRestore, login, logout }}>
      {inRouter && <RouteChangeListener onRouteChange={handleRouteChange} />}
      {children}
      {user && permissionChange && (
        <PermissionChangeDialog
          change={permissionChange}
          onConfirm={acknowledgePermissionChange}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
